"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { Alert, AlertDescription } from "@/components/ui/design-system/alert";
import { Button } from "@/components/ui/design-system/button";
import { Body, Display } from "@/components/ui/design-system/text";
import { cn } from "@/lib/utils";
import { CreatorAuthFrame } from "../CreatorAuthFrame";
import { QUIET_HOVER, QUIET_OUTLINE } from "../one/chrome";
import { BrandDirectory } from "./BrandDirectory";

/**
 * What the auth gate shows someone who may choose brands but is not on one
 * yet: a signed applicant, or a creator whose programs have all ended.
 *
 * The sign-in page's frame rather than the portal shell, because there is no
 * program yet for the shell's navigation to point at. The ways out stay
 * under the list, as on the gate's other screens.
 */
export function BrandChoiceScreen({
  inboxHref,
  signOutError,
  onSignOut,
}: {
  /** Set for a creator with a profile, whose inbox still works. */
  inboxHref: string | null;
  signOutError: string | null;
  onSignOut: () => void;
}) {
  const t = useTranslations("brands.choose");
  const gate = useTranslations("authGate");
  return (
    <CreatorAuthFrame>
      <div className="flex flex-col gap-nf-ds-block">
        <header className="flex min-w-0 flex-col items-center gap-nf-ds-2 text-center">
          <Display as="h1" className="text-balance">
            {t("title")}
          </Display>
          <Body muted className="text-pretty">
            {t("lede")}
          </Body>
        </header>

        <BrandDirectory />

        <div className="flex w-full flex-col gap-nf-ds-3">
          {inboxHref ? (
            <Button
              asChild
              variant="outline"
              className={cn("w-full", QUIET_OUTLINE)}
            >
              <Link href={inboxHref}>{gate("openInbox")}</Link>
            </Button>
          ) : null}
          <Button
            variant="ghost"
            className={cn("w-full", QUIET_HOVER)}
            onClick={onSignOut}
          >
            {gate("useAnotherAccount")}
          </Button>
        </div>

        {signOutError ? (
          <Alert variant="err" role="alert" className="w-full">
            <AlertDescription>{signOutError}</AlertDescription>
          </Alert>
        ) : null}
      </div>
    </CreatorAuthFrame>
  );
}
